/**
 * LocalStorage-Service für die Datenverwaltung
 */
// Importiere die Standardwerte und umgehe potenzielle JSON-Parsing-Probleme
let defaultValues;
try {
  defaultValues = require('../default_values.json');
  console.log('Geladene default_values.json:', defaultValues);
} catch (error) {
  console.error('Fehler beim Laden der default_values.json:', error);
  // Fallback-Werte definieren, falls die JSON-Datei nicht geladen werden kann
  defaultValues = {
    currentFee: {
      id: 1,
      percentage_fee: 3.3,
      fixed_fee: 0.65
    },
    paymentMethods: [
      {
        id: 1,
        name: 'PayPal',
        percentage_fee: 1.95,
        fixed_fee: 0.45,
        distribution_percentage: 90.6
      },
      {
        id: 2,
        name: 'Klarna',
        percentage_fee: 2.99,
        fixed_fee: 0.35,
        distribution_percentage: 0.61
      },
      {
        id: 3,
        name: 'Stripe',
        percentage_fee: 1.5,
        fixed_fee: 0.25,
        distribution_percentage: 8.79
      }
    ],
    payoutConditions: [
      {
        id: 1,
        percentage_fee: 0.25,
        fixed_fee: 0.1,
        active_account_fee: 2
      }
    ],
    cartValues: {
      basePrice: "16.36",
      ticketCount: null,
      cartValue: null
    }
  };
}

const LocalStorageService = {
  // Store-Keys
  KEYS: {
    PAYMENT_METHODS: 'paymentMethods',
    PAYOUT_CONDITIONS: 'payoutConditions',
    COMPETITORS: 'competitors',
    PACKAGES: 'packages',
    CURRENT_FEE: 'currentFee'
  },
  
  /**
   * Daten aus dem localStorage abrufen
   * @param {string} key - Der Schlüssel für den localStorage
   * @param {any} defaultValue - Standardwert, falls keine Daten vorhanden sind
   * @returns {any} Die gespeicherten Daten oder der Standardwert
   */  getData(key, defaultValue = []) {
    try {
      const storedData = localStorage.getItem(key);
      const parsedData = storedData ? JSON.parse(storedData) : defaultValue;
      console.log(`LocalStorageService.getData(${key}):`, parsedData);
      return parsedData;
    } catch (error) {
      console.error(`Fehler beim Lesen von ${key} aus localStorage:`, error);
      return defaultValue;
    }
  },
  
  /**
   * Daten im localStorage speichern
   * @param {string} key - Der Schlüssel für den localStorage
   * @param {any} data - Die zu speichernden Daten
   */  saveData(key, data) {
    try {
      console.log(`LocalStorageService.saveData(${key}):`, data);
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error(`Fehler beim Speichern von ${key} in localStorage:`, error);
    }
  },
  
  /**
   * Zahlungsmethoden abrufen
   * @returns {Array} Liste der Zahlungsmethoden
   */
  getPaymentMethods() {
    return this.getData(this.KEYS.PAYMENT_METHODS, []);
  },
  
  /**
   * Zahlungsmethode anhand der ID abrufen
   * @param {number} id - ID der Zahlungsmethode
   * @returns {Object|null} Die gefundene Zahlungsmethode oder null
   */
  getPaymentMethodById(id) {
    const paymentMethods = this.getPaymentMethods();
    return paymentMethods.find(method => method.id === parseInt(id)) || null;
  },
  
  /**
   * Zahlungsmethode speichern
   * @param {Object} paymentMethod - Die zu speichernde Zahlungsmethode
   * @returns {Object} Die gespeicherte Zahlungsmethode mit ID
   */  savePaymentMethod(paymentMethod) {
    const paymentMethods = this.getPaymentMethods();
    
    // Stelle sicher, dass numerische Werte als Zahlen gespeichert werden
    const processedPaymentMethod = {
      ...paymentMethod,
      id: parseInt(paymentMethod.id || 0),
      percentage_fee: parseFloat(paymentMethod.percentage_fee || 0),
      fixed_fee: parseFloat(paymentMethod.fixed_fee || 0),
      distribution_percentage: parseFloat(paymentMethod.distribution_percentage || 0)
    };
    
    if (processedPaymentMethod.id) {
      // Aktualisiere bestehende Zahlungsmethode
      const index = paymentMethods.findIndex(method => method.id === processedPaymentMethod.id);
      if (index !== -1) {
        paymentMethods[index] = processedPaymentMethod;
      }
    } else {
      // Neue Zahlungsmethode
      const maxId = paymentMethods.reduce((max, method) => Math.max(max, method.id || 0), 0);
      processedPaymentMethod.id = maxId + 1;
      paymentMethods.push(processedPaymentMethod);
    }
    
    this.saveData(this.KEYS.PAYMENT_METHODS, paymentMethods);
    return processedPaymentMethod;
  },
  
  /**
   * Zahlungsmethode löschen
   * @param {number} id - ID der zu löschenden Zahlungsmethode
   * @returns {boolean} true bei Erfolg, false bei Fehler
   */
  deletePaymentMethod(id) {
    const paymentMethods = this.getPaymentMethods();
    const filteredMethods = paymentMethods.filter(method => method.id !== parseInt(id));
    
    if (filteredMethods.length < paymentMethods.length) {
      this.saveData(this.KEYS.PAYMENT_METHODS, filteredMethods);
      return true;
    }
    return false;
  },
  
  /**
   * Auszahlungsbedingungen abrufen
   * @returns {Array} Liste der Auszahlungsbedingungen
   */
  getPayoutConditions() {
    return this.getData(this.KEYS.PAYOUT_CONDITIONS, []);
  },
  
  /**
   * Auszahlungsbedingung anhand der ID abrufen
   * @param {number} id - ID der Auszahlungsbedingung
   * @returns {Object|null} Die gefundene Auszahlungsbedingung oder null
   */
  getPayoutConditionById(id) {
    const payoutConditions = this.getPayoutConditions();
    return payoutConditions.find(condition => condition.id === parseInt(id)) || null;
  },
  
  /**
   * Auszahlungsbedingung speichern
   * @param {Object} payoutCondition - Die zu speichernde Auszahlungsbedingung
   * @returns {Object} Die gespeicherte Auszahlungsbedingung mit ID
   */
  savePayoutCondition(payoutCondition) {
    const payoutConditions = this.getPayoutConditions();
    
    if (payoutCondition.id) {
      // Aktualisiere bestehende Auszahlungsbedingung
      const index = payoutConditions.findIndex(condition => condition.id === parseInt(payoutCondition.id));
      if (index !== -1) {
        payoutConditions[index] = payoutCondition;
      }
    } else {
      // Neue Auszahlungsbedingung
      const maxId = payoutConditions.reduce((max, condition) => Math.max(max, condition.id || 0), 0);
      payoutCondition.id = maxId + 1;
      payoutConditions.push(payoutCondition);
    }
    
    this.saveData(this.KEYS.PAYOUT_CONDITIONS, payoutConditions);
    return payoutCondition;
  },
  
  /**
   * Auszahlungsbedingung löschen
   * @param {number} id - ID der zu löschenden Auszahlungsbedingung
   * @returns {boolean} true bei Erfolg, false bei Fehler
   */
  deletePayoutCondition(id) {
    const payoutConditions = this.getPayoutConditions();
    const filteredConditions = payoutConditions.filter(condition => condition.id !== parseInt(id));
    
    if (filteredConditions.length < payoutConditions.length) {
      this.saveData(this.KEYS.PAYOUT_CONDITIONS, filteredConditions);
      return true;
    }
    return false;
  },
  
  /**
   * Wettbewerber abrufen
   * @returns {Array} Liste der Wettbewerber
   */  getCompetitors() {
    const competitors = this.getData(this.KEYS.COMPETITORS, []);
    console.log('LocalStorageService.getCompetitors:', competitors);
    return competitors;
  },
  
  /**
   * Wettbewerber anhand der ID abrufen
   * @param {number} id - ID des Wettbewerbers
   * @returns {Object|null} Der gefundene Wettbewerber oder null
   */
  getCompetitorById(id) {
    const competitors = this.getCompetitors();
    return competitors.find(competitor => competitor.id === parseInt(id)) || null;
  },
  
  /**
   * Wettbewerber speichern
   * @param {Object} competitor - Der zu speichernde Wettbewerber
   * @returns {Object} Der gespeicherte Wettbewerber mit ID
   */
  saveCompetitor(competitor) {
    const competitors = this.getCompetitors();
    
    if (competitor.id) {
      // Aktualisiere bestehenden Wettbewerber
      const index = competitors.findIndex(comp => comp.id === parseInt(competitor.id));
      if (index !== -1) {
        competitors[index] = competitor;
      }
    } else {
      // Neuer Wettbewerber
      const maxId = competitors.reduce((max, comp) => Math.max(max, comp.id || 0), 0);
      competitor.id = maxId + 1;
      competitors.push(competitor);
    }
    
    this.saveData(this.KEYS.COMPETITORS, competitors);
    return competitor;
  },
  
  /**
   * Wettbewerber löschen
   * @param {number} id - ID des zu löschenden Wettbewerbers
   * @returns {boolean} true bei Erfolg, false bei Fehler
   */
  deleteCompetitor(id) {
    const competitors = this.getCompetitors();
    const filteredCompetitors = competitors.filter(comp => comp.id !== parseInt(id));
    
    if (filteredCompetitors.length < competitors.length) {
      this.saveData(this.KEYS.COMPETITORS, filteredCompetitors);
      // Lösche auch alle zugehörigen Pakete
      const packages = this.getPackages();
      const filteredPackages = packages.filter(pkg => pkg.competitor_id !== parseInt(id));
      this.saveData(this.KEYS.PACKAGES, filteredPackages);
      return true;
    }
    return false;
  },
  
  /**
   * Pakete abrufen
   * @param {number} competitorId - Optional: ID des Wettbewerbers, für den Pakete abgerufen werden sollen
   * @returns {Array} Liste der Pakete
   */
  getPackages(competitorId = null) {
    const packages = this.getData(this.KEYS.PACKAGES, []);
    return competitorId 
      ? packages.filter(pkg => pkg.competitor_id === parseInt(competitorId)) 
      : packages;
  },
  
  /**
   * Paket anhand der ID abrufen
   * @param {number} id - ID des Pakets
   * @returns {Object|null} Das gefundene Paket oder null
   */
  getPackageById(id) {
    const packages = this.getPackages();
    return packages.find(pkg => pkg.id === parseInt(id)) || null;
  },
  
  /**
   * Paket speichern
   * @param {Object} packageData - Das zu speichernde Paket
   * @returns {Object} Das gespeicherte Paket mit ID
   */
  savePackage(packageData) {
    const packages = this.getPackages();
    
    if (packageData.id) {
      // Aktualisiere bestehendes Paket
      const index = packages.findIndex(pkg => pkg.id === parseInt(packageData.id));
      if (index !== -1) {
        packages[index] = packageData;
      }
    } else {
      // Neues Paket
      const maxId = packages.reduce((max, pkg) => Math.max(max, pkg.id || 0), 0);
      packageData.id = maxId + 1;
      packages.push(packageData);
    }
    
    this.saveData(this.KEYS.PACKAGES, packages);
    return packageData;
  },
  
  /**
   * Paket löschen
   * @param {number} id - ID des zu löschenden Pakets
   * @returns {boolean} true bei Erfolg, false bei Fehler
   */
  deletePackage(id) {
    const packages = this.getPackages();
    const filteredPackages = packages.filter(pkg => pkg.id !== parseInt(id));
    
    if (filteredPackages.length < packages.length) {
      this.saveData(this.KEYS.PACKAGES, filteredPackages);
      return true;
    }
    return false;
  },
  
  /**
   * Aktuelle Gebühreneinstellung abrufen
   * @returns {Object} Die aktuelle Gebühreneinstellung
   */  getCurrentFee() {
    return this.getData(this.KEYS.CURRENT_FEE, {
      id: 1,
      percentage_fee: 3.3,
      fixed_fee: 0.65
    });
  },
  
  /**
   * Aktuelle Gebühreneinstellung speichern
   * @param {Object} feeData - Die zu speichernde Gebühreneinstellung
   * @returns {Object} Die gespeicherte Gebühreneinstellung
   */
  saveCurrentFee(feeData) {
    // Stelle sicher, dass die ID immer 1 ist (es gibt nur eine aktuelle Gebühreneinstellung)
    feeData.id = 1;
    this.saveData(this.KEYS.CURRENT_FEE, feeData);
    return feeData;
  },
    /**
   * Selektiver Datenexport
   * @param {Object} options - Optionen für den Export (welche Daten exportiert werden sollen)
   * @returns {Object} Die ausgewählten gespeicherten Daten
   */  exportData(options = {}) {
    const exportData = {
      exportDate: new Date().toISOString(),
      version: '1.1'
    };

    // Aktuelle NIGHTOWL Gebühr
    if (options.currentFee) {
      exportData.currentFee = this.getCurrentFee();
    }

    // Zahlungsmethoden
    if (options.paymentMethods) {
      exportData.paymentMethods = this.getPaymentMethods();
    }

    // Nur Zahlungsmethoden-Verteilung
    if (options.paymentDistribution && !options.paymentMethods) {
      const methods = this.getPaymentMethods();
      exportData.paymentDistribution = methods.map(method => ({
        id: method.id,
        name: method.name,
        distribution_percentage: method.distribution_percentage || 0
      }));
    }

    // Wettbewerber und deren Pakete
    if (options.competitors) {
      console.log('Exportiere Wettbewerber...');
      const competitors = this.getCompetitors();
      console.log('Wettbewerber zum Exportieren:', competitors);
      exportData.competitors = competitors;
      
      if (options.packages !== false) {
        const packages = this.getPackages();
        console.log('Packages zum Exportieren:', packages);
        exportData.packages = packages;
      }
    }

    // Auszahlungseinstellungen
    if (options.payoutConditions) {
      exportData.payoutConditions = this.getPayoutConditions();
    }

    // Warenkorb-Werte
    if (options.cartValues) {
      exportData.cartValues = {
        basePrice: localStorage.getItem('basePrice'),
        ticketCount: localStorage.getItem('ticketCount'),
        cartValue: localStorage.getItem('cartValue')
      };
    }

    // Allgemeine Werte
    if (options.generalValues) {
      exportData.generalValues = {
        ticketsPerEvent: localStorage.getItem('ticketsPerEvent'),
        ticketsPerMonth: localStorage.getItem('ticketsPerMonth'),
        eventsPerOrganizer: localStorage.getItem('eventsPerOrganizer')
      };
    }

    // Neue NIGHTOWL Gebühr
    if (options.newFee) {
      exportData.newFee = {
        percentage_fee: localStorage.getItem('newFeePercentage'),
        fixed_fee: localStorage.getItem('newFeeFixed')
      };
    }

    return exportData;
  },
  
  /**
   * Selektiver Datenimport
   * @param {Object} data - Die zu importierenden Daten
   * @param {Object} options - Optionen für den Import (welche Daten importiert werden sollen)
   * @returns {boolean} true bei Erfolg, false bei Fehler
   */
  importData(data, options = {}) {
    try {
      // Validiere Datenstruktur
      if (!data || typeof data !== 'object') {
        throw new Error('Ungültiges Datenformat');
      }
      
      // Aktuelle NIGHTOWL Gebühr
      if (options.currentFee && data.currentFee) {
        this.saveData(this.KEYS.CURRENT_FEE, data.currentFee);
      }

      // Zahlungsmethoden
      if (options.paymentMethods && Array.isArray(data.paymentMethods)) {
        this.saveData(this.KEYS.PAYMENT_METHODS, data.paymentMethods);
      }

      // Nur Zahlungsmethoden-Verteilung
      if (options.paymentDistribution && Array.isArray(data.paymentDistribution)) {
        const currentMethods = this.getPaymentMethods();
        // Aktualisiere nur die Verteilungsprozentwerte
        const updatedMethods = currentMethods.map(method => {
          const distMethod = data.paymentDistribution.find(m => m.name === method.name || m.id === method.id);
          if (distMethod) {
            return {
              ...method,
              distribution_percentage: distMethod.distribution_percentage
            };
          }
          return method;
        });
        this.saveData(this.KEYS.PAYMENT_METHODS, updatedMethods);
      }
      
      // Wettbewerber und deren Pakete
      if (options.competitors && Array.isArray(data.competitors)) {
        this.saveData(this.KEYS.COMPETITORS, data.competitors);
        
        if (options.packages !== false && Array.isArray(data.packages)) {
          this.saveData(this.KEYS.PACKAGES, data.packages);
        }
      }
      
      // Auszahlungseinstellungen
      if (options.payoutConditions && Array.isArray(data.payoutConditions)) {
        this.saveData(this.KEYS.PAYOUT_CONDITIONS, data.payoutConditions);
      }

      // Warenkorb-Werte
      if (options.cartValues && data.cartValues) {
        if (data.cartValues.basePrice) localStorage.setItem('basePrice', data.cartValues.basePrice);
        if (data.cartValues.ticketCount) localStorage.setItem('ticketCount', data.cartValues.ticketCount);
        if (data.cartValues.cartValue) localStorage.setItem('cartValue', data.cartValues.cartValue);
      }

      // Allgemeine Werte
      if (options.generalValues && data.generalValues) {
        if (data.generalValues.ticketsPerEvent) localStorage.setItem('ticketsPerEvent', data.generalValues.ticketsPerEvent);
        if (data.generalValues.ticketsPerMonth) localStorage.setItem('ticketsPerMonth', data.generalValues.ticketsPerMonth);
        if (data.generalValues.eventsPerOrganizer) localStorage.setItem('eventsPerOrganizer', data.generalValues.eventsPerOrganizer);
      }

      // Neue NIGHTOWL Gebühr
      if (options.newFee && data.newFee) {
        if (data.newFee.percentage_fee) localStorage.setItem('newFeePercentage', data.newFee.percentage_fee);
        if (data.newFee.fixed_fee) localStorage.setItem('newFeeFixed', data.newFee.fixed_fee);
      }
      
      return true;
    } catch (error) {
      console.error('Fehler beim Importieren der Daten:', error);
      return false;
    }
  },
  
  /**
   * Alle Daten exportieren (Kompatibilitätsmethode für ältere Versionen)
   * @returns {Object} Alle gespeicherten Daten
   */
  exportAllData() {
    return this.exportData({
      currentFee: true,
      paymentMethods: true,
      competitors: true,
      packages: true,
      payoutConditions: true,
      cartValues: true,
      generalValues: true,
      newFee: true
    });
  },
  
  /**
   * Alle Daten importieren (Kompatibilitätsmethode für ältere Versionen)
   * @param {Object} data - Die zu importierenden Daten
   * @returns {boolean} true bei Erfolg, false bei Fehler
   */
  importAllData(data) {
    return this.importData(data, {
      currentFee: true,
      paymentMethods: true,
      competitors: true,
      packages: true,
      payoutConditions: true,
      cartValues: true,
      generalValues: true,
      newFee: true
    });
  },
    /**
   * Setzt bestimmte Daten auf Standardwerte zurück
   * @param {Object} options - Welche Daten zurückgesetzt werden sollen
   * @returns {boolean} true bei Erfolg
   */
  resetToDefaults(options = {}) {
    try {
      // Zahlungsmethoden zurücksetzen
      if (options.paymentMethods) {
        const defaultPaymentMethods = this.getDefaultPaymentMethods();
        this.saveData(this.KEYS.PAYMENT_METHODS, defaultPaymentMethods);
      }

      // Auszahlungsbedingungen zurücksetzen
      if (options.payoutConditions) {
        const defaultPayoutConditions = this.getDefaultPayoutConditions();
        this.saveData(this.KEYS.PAYOUT_CONDITIONS, defaultPayoutConditions);
      }

      // Aktuelle Gebühr zurücksetzen
      if (options.currentFee) {
        const defaultCurrentFee = this.getDefaultCurrentFee();
        this.saveData(this.KEYS.CURRENT_FEE, defaultCurrentFee);
      }

      // Wettbewerber und Pakete zurücksetzen
      if (options.competitors) {
        this.saveData(this.KEYS.COMPETITORS, []);
        this.saveData(this.KEYS.PACKAGES, []);
      }

      // Warenkorb-Werte zurücksetzen
      if (options.cartValues) {
        localStorage.removeItem('basePrice');
        localStorage.removeItem('ticketCount');
        localStorage.removeItem('cartValue');
      }

      // Allgemeine Werte zurücksetzen
      if (options.generalValues) {
        localStorage.removeItem('ticketsPerEvent');
        localStorage.removeItem('ticketsPerMonth');
        localStorage.removeItem('eventsPerOrganizer');
      }

      // Neue NIGHTOWL Gebühr zurücksetzen
      if (options.newFee) {
        localStorage.removeItem('newFeePercentage');
        localStorage.removeItem('newFeeFixed');
      }

      return true;
    } catch (error) {
      console.error('Fehler beim Zurücksetzen der Daten:', error);
      return false;
    }
  },
  /**
   * Gibt die Standard-Zahlungsmethoden zurück
   * @returns {Array} Die Standard-Zahlungsmethoden
   */  getDefaultPaymentMethods() {
    // Stellen Sie sicher, dass wir die Werte aus der JSON-Datei lesen, wenn sie vorhanden sind
    if (defaultValues && defaultValues.paymentMethods && Array.isArray(defaultValues.paymentMethods)) {
      console.log('Lade Zahlungsmethoden aus default_values.json:', defaultValues.paymentMethods);
      return defaultValues.paymentMethods;
    }
    
    // Fallback zu hartcodierten Werten
    console.log('Verwende Fallback-Werte für Zahlungsmethoden');
    return [
      {
        id: 1,
        name: 'PayPal',
        percentage_fee: 1.95,
        fixed_fee: 0.45,
        distribution_percentage: 90.60
      },
      {
        id: 2,
        name: 'Klarna',
        percentage_fee: 2.99,
        fixed_fee: 0.35,
        distribution_percentage: 0.61
      },
      {
        id: 3,
        name: 'Stripe',
        percentage_fee: 1.50,
        fixed_fee: 0.25,
        distribution_percentage: 8.79
      }
    ];
  },

  /**
   * Gibt die Standard-Auszahlungsbedingungen zurück
   * @returns {Array} Die Standard-Auszahlungsbedingungen
   */  getDefaultPayoutConditions() {
    // Stellen Sie sicher, dass wir die Werte aus der JSON-Datei lesen, wenn sie vorhanden sind
    if (defaultValues && defaultValues.payoutConditions && Array.isArray(defaultValues.payoutConditions)) {
      console.log('Lade Auszahlungsbedingungen aus default_values.json:', defaultValues.payoutConditions);
      return defaultValues.payoutConditions;
    }
    
    // Fallback zu hartcodierten Werten
    console.log('Verwende Fallback-Werte für Auszahlungsbedingungen');
    return [
      {
        id: 1,
        percentage_fee: 0.25,
        fixed_fee: 0.10,
        active_account_fee: 2.00
      }
    ];
  },

  /**
   * Gibt die Standard aktuelle Gebühr zurück
   * @returns {Object} Die Standard aktuelle Gebühr
   */  
  getDefaultCurrentFee() {
    // Stellen Sie sicher, dass wir die Werte aus der JSON-Datei lesen, wenn sie vorhanden sind
    if (defaultValues && defaultValues.currentFee) {
      console.log('Lade aktuelle Gebühr aus default_values.json:', defaultValues.currentFee);
      return defaultValues.currentFee;
    }
    
    // Fallback zu hartcodierten Werten
    console.log('Verwende Fallback-Werte für aktuelle Gebühr');
    return {
      id: 1,
      percentage_fee: 3.3,
      fixed_fee: 0.65
    };
  },

  /**
   * Gibt die Standard-Wettbewerber zurück
   * @returns {Array} Die Standard-Wettbewerber
   */
  getDefaultCompetitors() {
    // Stellen Sie sicher, dass wir die Werte aus der JSON-Datei lesen, wenn sie vorhanden sind
    if (defaultValues && defaultValues.competitors && Array.isArray(defaultValues.competitors)) {
      console.log('Lade Wettbewerber aus default_values.json:', defaultValues.competitors);
      return defaultValues.competitors;
    }
    
    // Fallback zu leerer Liste
    console.log('Verwende leere Liste für Wettbewerber');
    return [];
  },

  /**
   * Gibt die Standard-Pakete zurück
   * @returns {Array} Die Standard-Pakete
   */
  getDefaultPackages() {
    // Stellen Sie sicher, dass wir die Werte aus der JSON-Datei lesen, wenn sie vorhanden sind
    if (defaultValues && defaultValues.packages && Array.isArray(defaultValues.packages)) {
      console.log('Lade Pakete aus default_values.json:', defaultValues.packages);
      return defaultValues.packages;
    }
    
    // Fallback zu leerer Liste
    console.log('Verwende leere Liste für Pakete');
    return [];
  },

  /**
   * Initialisiere den localStorage mit Standarddaten, falls noch keine Daten vorhanden sind
   */  initializeLocalStorageIfNeeded() {
    console.log('Initialisiere localStorage mit Standardwerten wenn nötig');
    
    // Prüfe, ob bereits Daten in localStorage existieren
    const hasPaymentMethods = localStorage.getItem(this.KEYS.PAYMENT_METHODS);
    const hasPayoutConditions = localStorage.getItem(this.KEYS.PAYOUT_CONDITIONS);
    const hasCompetitors = localStorage.getItem(this.KEYS.COMPETITORS);
    const hasCurrentFee = localStorage.getItem(this.KEYS.CURRENT_FEE);
    
    // Wenn keine Daten vorhanden sind, initialisiere mit Standardwerten
    if (!hasPaymentMethods) {
      const defaultPaymentMethods = this.getDefaultPaymentMethods();
      console.log('Speichere Standard-Zahlungsmethoden:', defaultPaymentMethods);
      this.saveData(this.KEYS.PAYMENT_METHODS, defaultPaymentMethods);
    }
    
    if (!hasPayoutConditions) {
      const defaultPayoutConditions = this.getDefaultPayoutConditions();
      console.log('Speichere Standard-Auszahlungsbedingungen:', defaultPayoutConditions);
      this.saveData(this.KEYS.PAYOUT_CONDITIONS, defaultPayoutConditions);
    }
      if (!hasCompetitors) {
      const defaultCompetitors = this.getDefaultCompetitors();
      console.log('Speichere Standard-Wettbewerber:', defaultCompetitors);
      this.saveData(this.KEYS.COMPETITORS, defaultCompetitors);
      
      // Pakete für Wettbewerber ebenfalls speichern
      const defaultPackages = this.getDefaultPackages();
      console.log('Speichere Standard-Pakete:', defaultPackages);
      this.saveData(this.KEYS.PACKAGES, defaultPackages);
    }
    
    if (!hasCurrentFee) {
      const defaultCurrentFee = this.getDefaultCurrentFee();
      console.log('Speichere Standard-Gebühren:', defaultCurrentFee);
      this.saveData(this.KEYS.CURRENT_FEE, defaultCurrentFee);
    }
    
    // Initialisiere weitere Werte aus der default_values.json
    if (defaultValues.cartValues) {
      console.log('Initialisiere Warenkorb-Werte aus default_values.json:', defaultValues.cartValues);
      if (!localStorage.getItem('basePrice')) localStorage.setItem('basePrice', defaultValues.cartValues.basePrice);
      if (!localStorage.getItem('ticketCount')) localStorage.setItem('ticketCount', defaultValues.cartValues.ticketCount);
      if (!localStorage.getItem('cartValue')) localStorage.setItem('cartValue', defaultValues.cartValues.cartValue);
    }
    
    if (defaultValues.generalValues) {
      console.log('Initialisiere allgemeine Werte aus default_values.json:', defaultValues.generalValues);
      if (!localStorage.getItem('ticketsPerEvent')) localStorage.setItem('ticketsPerEvent', defaultValues.generalValues.ticketsPerEvent);
      if (!localStorage.getItem('ticketsPerMonth')) localStorage.setItem('ticketsPerMonth', defaultValues.generalValues.ticketsPerMonth);
      if (!localStorage.getItem('eventsPerOrganizer')) localStorage.setItem('eventsPerOrganizer', defaultValues.generalValues.eventsPerOrganizer);
    }
    
    if (defaultValues.newFee) {
      if (!localStorage.getItem('newFeePercentage')) localStorage.setItem('newFeePercentage', defaultValues.newFee.percentage_fee);
      if (!localStorage.getItem('newFeeFixed')) localStorage.setItem('newFeeFixed', defaultValues.newFee.fixed_fee);
    }
  },
  
  /**
   * Bilder als Base64 speichern
   * @param {File} file - Die zu speichernde Datei
   * @returns {Promise<string>} Der Base64-String des Bildes
   */
  async saveImageAsBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }
};

export default LocalStorageService;
